import React, { useEffect, useRef } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5gantt from "@amcharts/amcharts5/gantt"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"

// Utils
import { createExportMenu } from "../utils/utils"
import { buildGanttData, getGanttTimeRange, GanttColumnRoles } from "./utils"

// Style
import { GanttContainer, GanttHint, GanttSkipped } from "./index.style"

interface GanttChartV2Props {
  /** Lignes brutes du widget, indexées par `valueId` de colonne (mode « tableau »). */
  dataChart?: Array<Record<string, unknown>>
  /** Quelle colonne joue quel rôle. Réglé dans l'onglet Données du widget. */
  ganttColumnRoles?: GanttColumnRoles
  colorSetTheme?: string[]
  /** Largeur de la colonne de libellés, en pourcentage de la largeur du widget. */
  ganttSidebarWidthPercent?: number
  /** Exclure samedi et dimanche du calcul des durées. */
  ganttExcludeWeekends?: boolean
  ganttDurationUnit?: "year" | "month" | "week" | "day" | "hour" | "minute" | "second"
  enableExportMenu?: boolean
  widgetTitle?: string
  idChart?: string
  width?: string
  height?: string
  /**
   * ⚠️ Les widgets ne reçoivent PAS `trad` : `containers/Widget` ne passe que `widgetTraduction`,
   * le sous-ensemble construit par `getWidgetTraduction(widgetType, languageId)`. Une clé absente de
   * ce sous-ensemble laisse la chaîne de repli anglaise s'afficher, sans erreur ni log.
   */
  widgetTraduction?: { [key: string]: string }
  /** Drill-down : appelé quand l'utilisateur clique sur une barre. categoryValue = idSample de la ligne. */
  onSegmentClick?: (payload: { categoryValue: string; seriesIndex?: number }) => void
}

const GanttChartV2: React.FC<GanttChartV2Props> = ({
  dataChart = [],
  ganttColumnRoles,
  colorSetTheme = ["#677CC1", "#53AC86", "#FFC552"],
  ganttSidebarWidthPercent = 30,
  ganttExcludeWeekends = false,
  ganttDurationUnit = "day",
  enableExportMenu = false,
  widgetTitle = "gantt",
  idChart,
  width = "100%",
  height = "100%",
  widgetTraduction,
  onSegmentClick,
}) => {
  const ID = useRef(idChart || `GanttChartV2${Math.random()}`).current
  const rootRef = useRef<am5.Root | null>(null)
  const ganttRef = useRef<am5gantt.Gantt | null>(null)
  // Ref pour éviter de recréer le graphe quand onSegmentClick change
  const onSegmentClickRef = useRef(onSegmentClick)
  useEffect(() => { onSegmentClickRef.current = onSegmentClick }, [onSegmentClick])
  /** Le cadrage initial de l'axe a-t-il déjà été posé ? Voir `applyData`. */
  const framedRef = useRef(false)

  const {
    categories,
    tasks,
    skippedRows,
    reversedRows,
    zeroLengthRows,
    sameColumnForStartAndEnd,
    unreadableSample,
  } = buildGanttData(
    dataChart,
    ganttColumnRoles,
    // La MÊME fonction que celle dont amCharts se sert en interne
    // (`GanttSeries.getUnitDuration` fait `$time.getDuration(durationUnit)`), pour qu'aucune
    // divergence d'arrondi ne soit possible entre la durée que je calcule et celle qu'il applique.
    am5.time.getDuration(ganttDurationUnit)
  )
  const hasEndRole = Boolean(ganttColumnRoles?.end)
  const isConfigured = Boolean(ganttColumnRoles?.label && ganttColumnRoles?.start)

  // Deux empreintes, pour ne réagir qu'aux vrais changements. Un effet sans dépendances (le motif
  // habituel de cette bibliothèque) tournerait à chaque rendu du dashboard et remettrait l'axe sur
  // l'étendue calculée : le zoom et le défilement de l'utilisateur seraient annulés en continu.
  const dataKey = JSON.stringify({ categories, tasks })
  const settingsKey = JSON.stringify([
    colorSetTheme,
    ganttSidebarWidthPercent,
    ganttExcludeWeekends,
    ganttDurationUnit,
  ])

  const applySettings = (gantt: am5gantt.Gantt, root: am5.Root) => {
    gantt.setAll({
      // Un dashboard n'a AUCUN chemin d'enregistrement : laisser déplacer une barre laisserait
      // croire à une modification de la fiche. Le Gantt y est donc en lecture seule.
      editable: false,
      durationUnit: ganttDurationUnit,
      excludeWeekends: ganttExcludeWeekends,
      sidebarWidth: am5.percent(ganttSidebarWidthPercent),
      colors: am5.ColorSet.new(root, {
        colors: colorSetTheme.map(color => am5.color(color)),
      }),
    })

    // Les commandes d'édition n'ont pas de sens en lecture seule : ajouter, supprimer, lier,
    // recolorier écriraient dans un graphe que personne ne sauvegarde.
    gantt.addButton.set("forceHidden", true)
    gantt.clearButton.set("forceHidden", true)
    gantt.linkButton.set("forceHidden", true)
    gantt.colorPickerButton.set("forceHidden", true)
    gantt.editButton.set("forceHidden", true)

    // `editable: false` rend les contrôles de la colonne de gauche INACTIFS, pas invisibles :
    // `_toggleEditable` fait `controlsContainers.template.set("forceInactive", true)`. On voit donc
    // encore le compteur de durée et la jauge d'avancement — un « 0 » devant chaque libellé — sans
    // pouvoir s'en servir. Dans un dashboard, ces contrôles ne peuvent RIEN faire : on les masque.
    const sidebarRenderer = gantt.yAxis.get("renderer")
    sidebarRenderer.controlsContainers.template.set("forceHidden", true)
  }

  const applyData = (gantt: am5gantt.Gantt) => {
    gantt.yAxis.data.setAll(categories)
    gantt.series.data.setAll(tasks)

    // Cadrage INITIAL seulement : sans lui l'axe s'ouvre sur la période courante, qui peut ne
    // contenir aucune barre. Mais le réappliquer à chaque mise à jour écrase le zoom de
    // l'utilisateur ET provoque une réanimation visible — le « clignotement » constaté à l'écran le
    // 2026-08-19, où `applyData` tournait 3 fois.
    if (!framedRef.current) {
      const range = getGanttTimeRange(tasks)
      if (range) {
        gantt.xAxis.set("min", range.start)
        gantt.xAxis.set("max", range.end)
        framedRef.current = true
      }
    }
  }

  useEffect(() => {
    if (!isConfigured) return

    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root
    root.setThemes([am5themes_Animated.new(root)])

    const gantt = root.container.children.push(am5gantt.Gantt.new(root, {}))
    ganttRef.current = gantt

    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataChart,
    })

    applySettings(gantt, root)

    // Drill-down : clic sur une barre OU un losange (milestone, duration=0).
    // `idSample` dans le dataContext vaut l'idSample MongoDB de la ligne (ajouté par le widget
    // ETL via generateETLRequestJsonFromWidgetInfo). Widget/index.js filtre alors le DrillDown
    // sur { fieldUUID: "idSample", value: idSample } → exactement 1 ligne.
    //
    // Problème de timing : `datavalidated` précède `makeColumn`. Quand l'événement
    // se déclenche, ni `graphics` ni `zeroRectangle` n'existent encore dans les
    // dataItems — ils sont créés plus tard, lors du RENDU. On s'attache donc aux
    // TEMPLATES (ListTemplate), qui propagent leurs événements à toutes les instances
    // présentes et futures, sans aucune contrainte de timing.
    //
    // Deux templates distincts :
    //  • columns.template    → colonne des tâches normales.
    //    editable=false → _toggleEditable pose draggable=false, retirant l'interactivité
    //    implicite. On force interactive:true pour que "click" soit reçu.
    //  • zeroRectangles.template → losange des milestones (duration=0).
    //    Non touché par _toggleEditable, reste draggable=true (interactif par défaut).
    //
    // Récupération du dataItem :
    //  • Colonne : _dataItem posé par super.makeColumn (accès direct via ev.target._dataItem).
    //  • zeroRectangle : pas de _dataItem propre — mais son Container parent en a un
    //    (container._setDataItem(dataItem) dans makeColumn). On remonte via parent.
    gantt.series.columns.template.setAll({ interactive: true, cursorOverStyle: "pointer" })
    gantt.series.columns.template.events.on("click", (ev: any) => {
      const di = ev.target?._dataItem ?? ev.target?.parent?._dataItem
      const idSample = (di?.dataContext as any)?.idSample
      if (idSample != null && onSegmentClickRef.current) {
        onSegmentClickRef.current({ categoryValue: String(idSample) })
      }
    })

    const seriesAny = gantt.series as any
    if (seriesAny.zeroRectangles) {
      seriesAny.zeroRectangles.template.set("cursorOverStyle", "pointer")
      seriesAny.zeroRectangles.template.events.on("click", (ev: any) => {
        const di = ev.target?.parent?._dataItem ?? ev.target?._dataItem
        const idSample = (di?.dataContext as any)?.idSample
        if (idSample != null && onSegmentClickRef.current) {
          onSegmentClickRef.current({ categoryValue: String(idSample) })
        }
      })
    }

    applyData(gantt)
    gantt.appear()

    return () => {
      rootRef.current?.dispose()
      rootRef.current = null
      ganttRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigured])

  useEffect(() => {
    const gantt = ganttRef.current
    const root = rootRef.current
    if (!gantt || !root) return

    applySettings(gantt, root)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsKey])

  useEffect(() => {
    if (!ganttRef.current) return

    applyData(ganttRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey])

  if (!isConfigured) {
    return (
      <GanttContainer style={{ width, height }}>
        <GanttHint>
          <i className="fa-regular fa-circle-info" />
          <span>
            {widgetTraduction?.PICK_COLUMNS ||
              "Choose the label column and the start date column in the widget configuration."}
          </span>
        </GanttHint>
      </GanttContainer>
    )
  }

  return (
    <GanttContainer style={{ width, height }}>
      <div id={ID} className="ganttChart" />
      {/*
        Trois diagnostics distincts, et c'est volontaire : ils ont trois causes et trois gestes
        correctifs différents. Les confondre — ou n'en montrer aucun — fait chercher du mauvais côté.
        Vécu le 2026-08-19 : un graphe entier de losanges a été pris pour un défaut de conversion de
        dates alors que les deux rôles début/fin étaient inversés.
      */}
      {!hasEndRole && (
        <GanttSkipped>
          {widgetTraduction?.NO_END_ROLE ||
            "No end date column selected: every row is shown as a milestone."}
        </GanttSkipped>
      )}
      {sameColumnForStartAndEnd && (
        <GanttSkipped>
          {widgetTraduction?.SAME_COLUMN ||
            "The same column is set as start and end date: every bar has zero length."}
        </GanttSkipped>
      )}
      {!sameColumnForStartAndEnd && zeroLengthRows > 0 && zeroLengthRows === tasks.length && (
        <GanttSkipped>
          {widgetTraduction?.ALL_ZERO_LENGTH ||
            "Every row has the same start and end date: all bars have zero length."}
        </GanttSkipped>
      )}
      {reversedRows > 0 && (
        <GanttSkipped>
          {`${reversedRows} ${
            widgetTraduction?.REVERSED_ROWS ||
            "rows have an end date before the start date — start/end roles may be swapped."
          }`}
        </GanttSkipped>
      )}
      {skippedRows > 0 && (
        <GanttSkipped title={widgetTraduction?.SKIPPED_ROWS_HINT}>
          {`${skippedRows} ${
            widgetTraduction?.SKIPPED_ROWS || "rows without a usable date were ignored"
          }`}
          {unreadableSample ? ` — « ${unreadableSample} »` : ""}
        </GanttSkipped>
      )}
    </GanttContainer>
  )
}

export default GanttChartV2
